import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FileText,
  User,
  Building,
  DollarSign,
  Ship,
  Package,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Truck,
  FileCheck,
} from 'lucide-react';

// Incomplete Fields Checker Hook
const useIncompleteFieldsChecker = formData => {
  // Define field categories and their display information
  const fieldCategories = useMemo(
    () => ({
      certificateInfo: {
        title: 'Certificate Information',
        icon: <FileText size={16} />,
        color: '#6366f1',
        fields: {
          RegistrationNumber: 'Registration Number',
        //   ReferenceNumber: 'Reference Number ⚠️ Required', // REMOVED: No longer required
          IssuerName: 'Issuer Name ⚠️ Required',
          IssuingOffice: 'Issuing Office ⚠️ Required',
          AssessmentDate: 'Assessment Date ⚠️ Required',
        },
      },
      importerInfo: {
        title: 'Importer Information',
        icon: <User size={16} />,
        color: '#059669',
        fields: {
          ImporterName: 'Importer Name ⚠️ Required',
          ImporterAddress: 'Importer Address',
          ImporterCity: 'Importer City ⚠️ Required',
          ImporterCountry: 'Importer Country ⚠️ Required',
        },
      },
      exporterInfo: {
        title: 'Exporter Information',
        icon: <Building size={16} />,
        color: '#10b981',
        fields: {
          ExporterName: 'Exporter Name ⚠️ Required',
          ExporterAddress: 'Exporter Address ⚠️ Required',
          ExporterCountry: 'Exporter Country ⚠️ Required',
        },
      },
      invoiceInfo: {
        title: 'Invoice Information',
        icon: <DollarSign size={16} />,
        color: '#dc2626',
        fields: {
          InvoiceNumber: 'Invoice Number ⚠️ Required',
          InvoiceDate: 'Invoice Date ⚠️ Required',
          InvoiceValue: 'Invoice Value ⚠️ Required',
          InvoiceCurrency: 'Invoice Currency ⚠️ Required',
          DeclaredValuePerContainer: 'Declared Value Per Container ⚠️ Required',
          InvoiceValueFeeBasis: 'Invoice Value (Fee Basis) ⚠️ Required',
          Incoterms: 'Incoterms',
        },
      },
      // RESTRUCTURED: Split shipmentInfo into 3 balanced subcategories
      shipmentDetails: {
        title: 'Shipment Details',
        icon: <Ship size={16} />,
        color: '#3b82f6',
        fields: {
          ShipmentType: 'Shipment Type ⚠️ Required',
          Branch: 'Branch ⚠️ Required',
          CountryOrigin: 'Country of Origin ⚠️ Required',
          CountryOfShipment: 'Country of Shipment ⚠️ Required',
          PointOfEntry: 'Point of Entry ⚠️ Required',
        },
      },
      containerPackaging: {
        title: 'Container & Packaging',
        icon: <Truck size={16} />,
        color: '#8b5cf6',
        fields: {
          ContainersCount: 'Containers Count ⚠️ Required',
          PackingDetails: 'Packing Details ⚠️ Required',
          ContainerDetails: 'Container Details ⚠️ Required',
          NumberAndTypeOfPackage: 'Number and Type of Package ⚠️ Required',
          ContainerSealNumber: 'Container Seal Number',
        },
      },
      documentation: {
        title: 'Additional Shipment Information',
        icon: <FileCheck size={16} />,
        color: '#06b6d4',
        fields: {
          DocumentNumber: 'Document Number',
          DocumentDate: 'Document Date',
          BlNo: 'BL Number',
          BlDate: 'BL Date',
          Signee: 'Signee',
          Remarks: 'Remarks',
        },
      },
      itemsInfo: {
        title: 'Items Information',
        icon: <Package size={16} />,
        color: '#7c3aed',
        fields: {}, // Dynamic based on items
      },
    }),
    []
  );

  // Check for incomplete fields
  const incompleteFields = useMemo(() => {
    const result = {};

    // Check main form fields
    Object.keys(fieldCategories).forEach(categoryKey => {
      const category = fieldCategories[categoryKey];
      const incomplete = [];

      if (categoryKey === 'itemsInfo') {
        // Special handling for items
        formData.Items?.forEach((item, index) => {
          const itemIncomplete = [];

          // Required item fields (based on API validation)
          // Helper function to check if a value is empty (handles strings and other types)
          const isEmpty = (value) => {
            if (value === null || value === undefined) return true;
            if (typeof value === 'string') return value.trim() === '';
            if (typeof value === 'number') return false; // Numbers are not empty
            return !value; // For other types
          };

          if (isEmpty(item.Type)) {
            itemIncomplete.push(`Item ${index + 1}: Item Type ⚠️ Required`);
          }

          if (isEmpty(item.ItemDescription)) {
            itemIncomplete.push(
              `Item ${index + 1}: Item Description ⚠️ Required`
            );
          }

          if (isEmpty(item.Quantity)) {
            itemIncomplete.push(`Item ${index + 1}: Quantity ⚠️ Required`);
          }
          if (isEmpty(item.Origin)) {
            itemIncomplete.push(`Item ${index + 1}: Origin ⚠️ Required`);
          }
          if (isEmpty(item.StandardRefNumber)) {
            itemIncomplete.push(
              `Item ${index + 1}: Standard Reference Number ⚠️ Required`
            );
          }
          if (!item.GoodsValue || item.GoodsValue <= 0) {
            itemIncomplete.push(`Item ${index + 1}: Goods Value ⚠️ Required`);
          }
          if (isEmpty(item.Currency)) {
            itemIncomplete.push(`Item ${index + 1}: Currency ⚠️ Required`);
          }
          if (isEmpty(item.Packing)) {
            itemIncomplete.push(
              `Item ${index + 1}: Packing Method ⚠️ Required`
            );
          }

          // Optional item fields
          if (isEmpty(item.Brand)) {
            itemIncomplete.push(`Item ${index + 1}: Brand`);
          }
          if (isEmpty(item.BatchNumber)) {
            itemIncomplete.push(`Item ${index + 1}: Batch Number`);
          }
          if (isEmpty(item.SlotDate)) {
            itemIncomplete.push(`Item ${index + 1}: Slaughter Date`);
          }
          if (isEmpty(item.ProductionDate)) {
            itemIncomplete.push(`Item ${index + 1}: Production Date`);
          }
          if (isEmpty(item.ExpirationDate)) {
            itemIncomplete.push(`Item ${index + 1}: Expiration Date`);
          }

          incomplete.push(...itemIncomplete);
        });
      } else {
        // Check regular fields
        Object.keys(category.fields).forEach(fieldKey => {
          const value = formData[fieldKey];

          // Check for empty/incomplete values
          const isEmpty =
            value === null ||
            value === undefined ||
            (typeof value === 'string' && value.trim() === '') ||
            (typeof value === 'number' &&
              value === 0 &&
              fieldKey === 'ContainersCount');

          if (isEmpty) {
            incomplete.push(category.fields[fieldKey]);
          }
        });
      }

      if (incomplete.length > 0) {
        result[categoryKey] = {
          ...category,
          incompleteFields: incomplete,
          count: incomplete.length,
        };
      }
    });

    return result;
  }, [formData, fieldCategories]);

  const totalIncompleteCount = useMemo(() => {
    return Object.values(incompleteFields).reduce(
      (total, category) => total + category.count,
      0
    );
  }, [incompleteFields]);

  return {
    incompleteFields,
    totalIncompleteCount,
    hasIncompleteFields: totalIncompleteCount > 0,
  };
};

// Incomplete Fields Warning Component
const IncompleteFieldsWarning = ({ formData, isDarkMode }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { incompleteFields, totalIncompleteCount, hasIncompleteFields } =
    useIncompleteFieldsChecker(formData);

  // Separate required and optional fields for better display
  const requiredFieldsCount = useMemo(() => {
    return Object.values(incompleteFields).reduce((total, category) => {
      return (
        total +
        category.incompleteFields.filter(field => field.includes('⚠️ Required'))
          .length
      );
    }, 0);
  }, [incompleteFields]);

  const optionalFieldsCount = totalIncompleteCount - requiredFieldsCount;

  if (!hasIncompleteFields) {
    return (
      <Card
        sx={{
          mb: 4,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle
              size={24}
              style={{ color: '#10b981', marginRight: 12 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                fontWeight: 600,
              }}
            >
              ✅ All Fields Complete!
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#6b7280',
              mt: 1,
              ml: 4.5,
            }}
          >
            Perfect! You've filled in all the form fields.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Determine the primary color and message based on whether there are required fields missing
  const hasRequiredFields = requiredFieldsCount > 0;
  const primaryColor = hasRequiredFields ? '#ef4444' : '#f59e0b';
  const borderColor = hasRequiredFields
    ? 'rgba(239, 68, 68, 0.3)'
    : 'rgba(245, 158, 11, 0.3)';
  const bgGradient = hasRequiredFields
    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)'
    : 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)';

  return (
    <Card
      sx={{
        mb: 4,
        background: isDarkMode ? bgGradient : bgGradient,
        border: `2px solid ${borderColor}`,
        borderRadius: 3,
        boxShadow: hasRequiredFields
          ? '0 4px 20px rgba(239, 68, 68, 0.15)'
          : '0 4px 20px rgba(245, 158, 11, 0.15)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasRequiredFields ? (
              <AlertCircle
                size={24}
                style={{ color: primaryColor, marginRight: 12 }}
              />
            ) : (
              <AlertTriangle
                size={24}
                style={{ color: primaryColor, marginRight: 12 }}
              />
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  fontWeight: 600,
                }}
              >
                {hasRequiredFields
                  ? '🚨 Missing Required Fields'
                  : '⚠️ Incomplete Fields'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#6b7280',
                  mt: 0.5,
                }}
              >
                {hasRequiredFields && (
                  <>
                    {requiredFieldsCount} required field
                    {requiredFieldsCount !== 1 ? 's' : ''} missing
                    {optionalFieldsCount > 0 &&
                      ` • ${optionalFieldsCount} optional field${optionalFieldsCount !== 1 ? 's' : ''}`}
                  </>
                )}
                {!hasRequiredFields && (
                  <>
                    {optionalFieldsCount} optional field
                    {optionalFieldsCount !== 1 ? 's' : ''} not filled
                  </>
                )}
                {' • Click to '}
                {isExpanded ? 'collapse' : 'expand'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {hasRequiredFields && (
              <Chip
                label={`${requiredFieldsCount} Required`}
                size="small"
                sx={{
                  background:
                    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
            {optionalFieldsCount > 0 && (
              <Chip
                label={`${optionalFieldsCount} Optional`}
                size="small"
                sx={{
                  background: hasRequiredFields
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
            <IconButton size="small">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 3 }}>
            <Alert
              severity={hasRequiredFields ? 'error' : 'warning'}
              sx={{
                mb: 3,
                background: isDarkMode
                  ? `rgba(${hasRequiredFields ? '239, 68, 68' : '245, 158, 11'}, 0.1)`
                  : `rgba(${hasRequiredFields ? '239, 68, 68' : '245, 158, 11'}, 0.05)`,
                border: `1px solid rgba(${hasRequiredFields ? '239, 68, 68' : '245, 158, 11'}, 0.2)`,
              }}
            >
              <Typography variant="body2">
                {hasRequiredFields ? (
                  <>
                    <strong>Required fields must be completed</strong> before
                    the certificate can be created. Optional fields help provide
                    more complete documentation.
                  </>
                ) : (
                  <>
                    <strong>All required fields are complete!</strong> These
                    optional fields can be filled to provide more comprehensive
                    documentation.
                  </>
                )}
              </Typography>
            </Alert>

            {/* ENHANCED GRID LAYOUT with consistent heights */}
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
              {Object.entries(incompleteFields).map(
                ([categoryKey, category]) => (
                  <Grid item xs={12} sm={6} lg={4} key={categoryKey}>
                    <Card
                      sx={{
                        background: isDarkMode
                          ? 'rgba(30, 41, 59, 0.4)'
                          : 'rgba(255, 255, 255, 0.6)',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        borderRadius: 2,
                        height: '100%', // CARD HEIGHT FIX
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '200px', // MINIMUM HEIGHT FOR CONSISTENCY
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 2,
                          flex: 1, // FLEX GROWTH FOR EQUAL HEIGHT
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <Box sx={{ color: category.color, mr: 1 }}>
                            {category.icon}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: isDarkMode ? '#e2e8f0' : '#1f2937',
                              fontWeight: 600,
                            }}
                          >
                            {category.title}
                          </Typography>
                          <Chip
                            label={category.count}
                            size="small"
                            sx={{
                              ml: 'auto',
                              backgroundColor: category.color + '20',
                              color: category.color,
                              fontWeight: 600,
                              minWidth: 24,
                              height: 20,
                            }}
                          />
                        </Box>

                        <List dense sx={{ p: 0, flex: 1 }}>
                          {category.incompleteFields.map((field, index) => {
                            const isRequired = field.includes('⚠️ Required');
                            const cleanFieldName = field.replace(
                              ' ⚠️ Required',
                              ''
                            );

                            return (
                              <ListItem
                                key={index}
                                sx={{
                                  px: 0,
                                  py: 0.5,
                                  minHeight: 24,
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  {isRequired ? (
                                    <AlertCircle
                                      size={12}
                                      style={{ color: '#ef4444' }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: category.color,
                                      }}
                                    />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isRequired
                                          ? isDarkMode
                                            ? '#fca5a5'
                                            : '#dc2626'
                                          : isDarkMode
                                            ? '#cbd5e1'
                                            : '#475569',
                                        fontSize: '0.8rem',
                                        fontWeight: isRequired ? 600 : 400,
                                      }}
                                    >
                                      {cleanFieldName}
                                      {isRequired && (
                                        <Chip
                                          label="Required"
                                          size="small"
                                          sx={{
                                            ml: 1,
                                            height: 16,
                                            fontSize: '0.6rem',
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                    </Typography>
                                  }
                                  sx={{ margin: 0 }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              )}
            </Grid>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#6b7280',
                  fontStyle: 'italic',
                }}
              >
                💡 Fields will automatically disappear from this list as you
                fill them out
                {hasRequiredFields &&
                  ' • ⚠️ Required fields must be completed before saving'}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default IncompleteFieldsWarning;
